import { useCallback, useMemo, useRef, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import type { ChatMessage, LegacyGenerationJob } from "~/entities/chat";
import { SPACE } from "~/shared/lib/design-tokens";
import { cn } from "~/shared/lib/utils";
import { useChatErrorsStore } from "~/shared/store/useChatErrorsStore";

import { groupMessagesByDate } from "./group-messages-by-date";
import { countActiveAndErroredJobs } from "./message-job-counts";
import { MessageDateGroup } from "./MessageDateGroup";
import { MessageScrollNav } from "./MessageScrollNav";
import { SystemMessageRow } from "./SystemMessageRow";
import { useLoadOlderMessages } from "./useLoadOlderMessages";
import { useMessageGroupCollapse } from "./useMessageGroupCollapse";
import { useStickyScrollAnchor } from "./useStickyScrollAnchor";

const SESSION_STATUS_THRESHOLD = 2;

interface VirtualMessageListProps {
    messages: ChatMessage[];
    heroMedia: boolean;
    jobsByMessageId: Record<string, LegacyGenerationJob>;
    onRetry: (message: ChatMessage) => void;
    retryingMessageId?: string | null;
    // Лента держит хвост чата: более ранние сообщения подгружаются, когда
    // пользователь домотал до верха. Без пропов ведёт себя как раньше —
    // показывает то, что ей передали.
    hasOlderMessages?: boolean;
    isLoadingOlderMessages?: boolean;
    onLoadOlderMessages?: () => void;
    // Парент (ChatShell) может пробросить ref, в который мы публикуем
    // handleNavScroll — это даёт ChatHeader (in-chat search) возможность
    // скроллить к нужному message_id с auto-expand'ом свёрнутых дней,
    // не дублируя логику.
    scrollToMessageRef?: MutableRefObject<((messageId: string) => void) | null>;
}

export function VirtualMessageList({
    messages,
    heroMedia,
    jobsByMessageId,
    onRetry,
    retryingMessageId,
    scrollToMessageRef,
    hasOlderMessages = false,
    isLoadingOlderMessages = false,
    onLoadOlderMessages,
}: VirtualMessageListProps) {
    const { t } = useTranslation("chat");
    const messageIds = useMemo(() => messages.map((m) => m.id), [messages]);
    const groups = useMemo(() => groupMessagesByDate(messages), [messages]);
    // Нижнее сообщение — единственное, что видно сразу после открытия чата:
    // лента стартует у хвоста. Его медиа грузится приоритетно, потому что в
    // общей очереди оно конкурирует с полусотней медиа из истории и приезжает
    // последним — то есть ровно то, на что человек смотрит, появляется позже
    // всего. Остальные тайлы остаются ленивыми: грузить всю историю разом
    // дороже, чем ждать её по мере скролла.
    const priorityMessageId = messages[messages.length - 1]?.id;

    // Session-status: активные (queued/in_progress/recovering) и ошибочные
    // (failed/expired) джобы. Центральную пилюлю показываем когда активных ≥2.
    const { active, errored } = useMemo(() => countActiveAndErroredJobs(jobsByMessageId), [jobsByMessageId]);
    const showSessionStatus = active >= SESSION_STATUS_THRESHOLD;
    const errorsCount = useChatErrorsStore((s) => Object.keys(s.errors).length);

    // Общий на два хука: догрузка поднимает флаг на время вставки сверху,
    // sticky-якорь по нему отличает выросшую историю от нового сообщения.
    const isPrependingRef = useRef(false);
    const { scrollRef, endRef, isStickyBottomRef } = useStickyScrollAnchor({
        messageCount: messages.length,
        lastMessageId: messages[messages.length - 1]?.id,
        errorsCount,
        activeCount: active,
        erroredCount: errored,
        isPrependingRef,
    });
    // Стабильная ссылка: иначе наблюдатель за верхней кромкой пересоздавался бы
    // на каждый рендер ленты.
    const loadOlder = useCallback(() => onLoadOlderMessages?.(), [onLoadOlderMessages]);
    const { sentinelRef } = useLoadOlderMessages({
        scrollRef,
        messageCount: messages.length,
        hasMore: hasOlderMessages && !!onLoadOlderMessages,
        isLoading: isLoadingOlderMessages,
        onLoadMore: loadOlder,
        isPrependingRef,
        isStickyBottomRef,
    });
    const { collapsed, toggleGroup, collapsedKey, handleNavScroll } = useMessageGroupCollapse({
        groups,
        messageCount: messages.length,
        scrollRef,
        isStickyBottomRef,
        scrollToMessageRef,
    });

    return (
        // Внешний relative-родитель: MessageScrollNav сидит здесь и не
        // скроллится вместе с контентом. Сам список — отдельный
        // overflow-y-auto child.
        <div className="relative flex min-h-0 flex-1">
            {/* Пилюля догрузки висит НАД лентой, а не в её потоке: появившись и
                исчезнув внутри списка, она меняла бы его высоту — и позиция
                чтения уезжала бы на её же высоту. */}
            {isLoadingOlderMessages && (
                <div
                    className="pointer-events-none absolute top-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-zinc-900/85 px-3 py-1.5 text-xs text-zinc-400 backdrop-blur-sm"
                    data-testid="older-messages-loading"
                >
                    <Loader2 className="size-3.5 animate-spin" />
                    {t("history.loadingOlder")}
                </div>
            )}
            <div
                ref={scrollRef}
                className="scroll-touch scrollbar-hide flex-1 overflow-y-auto"
                data-testid="virtual-message-list"
            >
                <div
                    className={cn(
                        "py-6",
                        heroMedia ? SPACE.heroGutter : cn(SPACE.msgListX, "mx-auto max-w-4xl"),
                    )}
                >
                    {/* Верхняя кромка ленты: пересечение с ней запрашивает
                        следующую порцию более ранних сообщений. */}
                    <div ref={sentinelRef} aria-hidden data-testid="older-messages-sentinel" />
                    {groups.map((group) => (
                        <MessageDateGroup
                            key={group.dateKey}
                            group={group}
                            collapsed={!!collapsed[group.dateKey]}
                            onToggle={() => toggleGroup(group.dateKey)}
                            heroMedia={heroMedia}
                            jobsByMessageId={jobsByMessageId}
                            onRetry={onRetry}
                            retryingMessageId={retryingMessageId}
                            priorityMessageId={priorityMessageId}
                        />
                    ))}
                    {showSessionStatus && (
                        <SystemMessageRow
                            text={
                                errored > 0
                                    ? t("system.sessionStatusWithErrors", {
                                          tasks: active,
                                          errors: errored,
                                      })
                                    : t("system.sessionStatus", { tasks: active })
                            }
                            level={errored > 0 ? "warning" : "info"}
                        />
                    )}
                    <div ref={endRef} />
                </div>
            </div>

            <MessageScrollNav
                messageIds={messageIds}
                scrollContainerRef={scrollRef}
                onScrollTo={handleNavScroll}
                stickyBottomRef={isStickyBottomRef}
                collapsedKey={collapsedKey}
            />
        </div>
    );
}
